import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import type { Section } from '../types';

type MetricDrilldownContextType = {
  openMetricId: string | null;
  openSection: Section | null;
  openL2: (metricId: string) => void;
  openSectionL2: (section: Section) => void;
  close: () => void;
};

const MetricDrilldownContext = createContext<MetricDrilldownContextType | undefined>(
  undefined,
);

export const MetricDrilldownProvider = ({ children }: { children: ReactNode }) => {
  const [openMetricId, setOpenMetricId] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<Section | null>(null);

  const openL2 = useCallback((metricId: string) => {
    if (!metricId) return;
    setOpenSection(null);
    setOpenMetricId(metricId);
  }, []);

  const openSectionL2 = useCallback((section: Section) => {
    setOpenMetricId(null);
    setOpenSection(section);
  }, []);

  const close = useCallback(() => {
    setOpenMetricId(null);
    setOpenSection(null);
  }, []);

  const value = useMemo(
    () => ({
      openMetricId,
      openSection,
      openL2,
      openSectionL2,
      close,
    }),
    [openMetricId, openSection, openL2, openSectionL2, close],
  );

  return (
    <MetricDrilldownContext.Provider value={value}>{children}</MetricDrilldownContext.Provider>
  );
};

export const useMetricDrilldown = (): MetricDrilldownContextType => {
  const ctx = useContext(MetricDrilldownContext);
  if (!ctx) {
    throw new Error('useMetricDrilldown must be used within MetricDrilldownProvider');
  }
  return ctx;
};
